import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Timer, AlertTriangle,
  ListChecks, Package, Download, PackageX, PackageMinus, ChevronDown,
  Layers, FolderTree, Boxes, ArrowUpRight, ClipboardList, CheckCircle2, IndianRupee, ShoppingBag, TrendingUp, Users,
} from 'lucide-react'
import { todoStats } from '../../../Models/dashboardData'
import Chart from 'chart.js/auto'
import { fetchTransactions } from '../Transaction/services/transactionSlice'
import { fetchCategories } from '../Category/services/categorySlice'
import { fetchOrders } from '../Order/services/orderSlice'
import { fetchProducts } from '../Product/services/productSlice'
import { fetchCustomers } from '../Customer/services/customerSlice'

// To do list icons — matches todoStats ids in Models/dashboardData.js
const TODO_ICONS = {
  pendingOrders: <Package size={18} />,
  downloadLabels: <Download size={18} />,
  outOfStock: <PackageX size={18} />,
  lowStock: <PackageMinus size={18} />,
}

const TX = {
  success: { cls: 'badge-active', label: 'Paid' },
  pending: { cls: 'badge-pending', label: 'Pending' },
  failed: { cls: 'badge-danger', label: 'Failed' },
  refunded: { cls: 'badge-gold', label: 'Refunded' },
}

// Orders by status: je status tamara orders ma aave che te pramane badlo
const ORDER_STATUSES = [
  { key: 'pending', label: 'Pending', color: '#F59E0B' },
  { key: 'processing', label: 'Processing', color: '#06B6D4' },
  { key: 'shipped', label: 'Shipped', color: '#4F46E5' },
  { key: 'delivered', label: 'Delivered', color: '#10B981' },
  { key: 'cancelled', label: 'Cancelled', color: '#EF4444' },
]

const CAT_COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6']

// To do list: stock aa ke tena thi ochhu hoy to "Low Stock" ma ganay
const LOW_STOCK_LIMIT = 5

const formatMoney = (n, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 2 }).format(Number(n || 0))

const timeAgo = (d) => {
  if (!d) return '—'
  const t = new Date(d)
  if (isNaN(t)) return '—'
  const s = Math.floor((Date.now() - t.getTime()) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} d ago`
  return t.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}


// Revenue chart: monthly target (₹). Tamari jarur pramane badlo.
const MONTHLY_TARGET = 50000
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
const compact = (v) => (v >= 100000 ? `${+(v / 100000).toFixed(1)}L` : v >= 1000 ? `${+(v / 1000).toFixed(1)}k` : v)

/* Chart.js canvas hook: data badle tyare chart update thay */
function useChart(ref, build, deps) {
  const chartRef = useRef(null)
  useEffect(() => {
    if (!ref.current) return
    chartRef.current?.destroy()
    chartRef.current = new Chart(ref.current, build())
    return () => { chartRef.current?.destroy(); chartRef.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/* ── Donut chart (pure SVG) ── */
function Donut({ segments, total }) {
  const R = 54
  const C = 2 * Math.PI * R
  let offset = 0

  return (
    <div className="donut-wrap">
      <svg viewBox="0 0 140 140" className="donut-svg">
        <circle cx="70" cy="70" r={R} fill="none" stroke="var(--bg-raised)" strokeWidth="16" />
        {total > 0 && segments.map((s) => {
          const len = (s.value / total) * C
          const el = (
            <circle
              key={s.id} cx="70" cy="70" r={R} fill="none"
              stroke={s.color} strokeWidth="16" strokeLinecap="butt"
              strokeDasharray={`${Math.max(len - 2, 0)} ${C}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 70 70)"
            />
          )
          offset += len
          return el
        })}
      </svg>
      <div className="donut-center">
        <div className="donut-num">{total}</div>
        <div className="donut-lbl">Products</div>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const revenueRef = useRef(null)
  const userRef = useRef(null)
  const { timeStr, isWarn } = useOutletContext()

  const txState = useSelector((s) => s.transaction) || {}
  const txList = txState.list || []
  const txLoading = !!txState.isLoading
  const recentTx = txList.slice(0, 5) // API already latest-first

  const catState = useSelector((s) => s.category) || {}
  const categories = catState.list || []

  const orderState = useSelector((s) => s.order) || {}
  const orders = orderState.list || []
  const productState = useSelector((s) => s.product) || {}
  const products = productState.list || []

  const customerState = useSelector((s) => s.customer) || {}
  const customers = customerState.customers || []
  const customersLoading = !!customerState.isLoading

  const [openCat, setOpenCat] = useState(null)

  useEffect(() => {
    dispatch(fetchTransactions())
    dispatch(fetchCategories())
    dispatch(fetchOrders())
    dispatch(fetchProducts())
    dispatch(fetchCustomers())
  }, [dispatch])

  const loading = (orderState.isLoading && orders.length === 0) || (txLoading && txList.length === 0)
  const custLoading = customersLoading && customers.length === 0
  const year = new Date().getFullYear()

  /* ── Revenue Overview: paid transactions, monthly sum (current year) ── */
  const revenueMonthly = useMemo(() => {
    const m = Array(12).fill(0)
    txList.forEach((t) => {
      if (t.status !== 'success') return
      const d = new Date(t.paid_at || t.created_at)
      if (isNaN(d) || d.getFullYear() !== year) return
      m[d.getMonth()] += Number(t.amount || 0)
    })
    return m.map((v) => Math.round(v * 100) / 100)
  }, [txList, year])

  /* ── Customer Acquisition: last 7 days, new signups vs verified (real customers) ── */
  const customerWeekly = useMemo(() => {
    const today = new Date()
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(today); d.setDate(today.getDate() - (6 - i)); return d })
    const joined = days.map((d) => customers.filter((c) => { const t = new Date(c.created_at); return !isNaN(t) && sameDay(t, d) }))
    return {
      labels: days.map((d) => DAYS[d.getDay()]),
      fresh: joined.map((l) => l.length),
      verified: joined.map((l) => l.filter((c) => c.email_verified_at).length),
    }
  }, [customers])

  const baseOpts = (fmt) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#8B93B5', font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: 'rgba(128,128,128,.15)' }, ticks: { color: '#8B93B5', font: { size: 10 }, precision: 0, callback: fmt } },
    },
  })

  useChart(revenueRef, () => ({
    type: 'line',
    data: {
      labels: MONTHS,
      datasets: [
        { label: 'Revenue', data: revenueMonthly, borderColor: '#4F46E5', backgroundColor: 'rgba(79,70,229,.12)', fill: true, tension: 0.35, borderWidth: 2.5, pointRadius: 3 },
        { label: 'Target', data: Array(12).fill(MONTHLY_TARGET), borderColor: 'rgba(99,102,241,.45)', borderDash: [5, 4], borderWidth: 2, pointRadius: 0, fill: false },
      ],
    },
    options: { ...baseOpts((v) => `₹${compact(v)}`), interaction: { mode: 'index', intersect: false } },
  }), [loading, revenueMonthly])

  useChart(userRef, () => ({
    type: 'bar',
    data: {
      labels: customerWeekly.labels,
      datasets: [
        { label: 'New', data: customerWeekly.fresh, backgroundColor: 'rgba(79,70,229,.8)', borderRadius: 4 },
        { label: 'Verified', data: customerWeekly.verified, backgroundColor: 'rgba(6,182,212,.5)', borderRadius: 4 },
      ],
    },
    options: baseOpts(),
  }), [custLoading, customerWeekly])

  /* ── Sales Summary + Recent Orders (real data) ── */
  const salesSummary = useMemo(() => {
    const revenue = txList.filter((t) => t.status === 'success').reduce((n, t) => n + Number(t.amount || 0), 0)
        const delivered = orders.filter((o) => o.status === 'delivered').length
    return {
      revenue,
      orders: orders.length,
      avg: orders.length ? revenue / orders.length : 0,
      customers: customers.length,
      deliveredPct: orders.length ? Math.round((delivered / orders.length) * 100) : 0,
    }
  }, [txList, orders, customers])

  const recentOrders = useMemo(
    () => [...orders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6),
    [orders]
  )

  /* ── To do list: real counts (card ids match Models/dashboardData.js) ── */
  const todoCounts = useMemo(() => ({
    pendingOrders: orders.filter((o) => o.status === 'pending').length,
    // label download mate taiyar: packed (processing) orders je haji ship nathi thaya
    downloadLabels: orders.filter((o) => o.status === 'processing').length,
    outOfStock: products.filter((p) => !p.in_stock).length,
    lowStock: products.filter((p) => p.in_stock && Number(p.stock_qty) <= LOW_STOCK_LIMIT).length,
  }), [orders, products])

  const todoValue = (id) => {
    const fromOrders = id === 'pendingOrders' || id === 'downloadLabels'
    const stillLoading = fromOrders
      ? orderState.isLoading && orders.length === 0
      : productState.isLoading && products.length === 0
    return stillLoading ? '…' : (todoCounts[id] ?? 0)
  }

  /* ── Stock Alerts: out of stock + low stock products (sauthi ochha stock pehla) ── */
  const stockAlerts = useMemo(() => {
    return products
      .filter((p) => !p.in_stock || Number(p.stock_qty) <= LOW_STOCK_LIMIT)
      .map((p) => ({
        id: p.id,
        name: p.name || p.title || `Product #${p.id}`,
        qty: Number(p.stock_qty || 0),
        out: !p.in_stock || Number(p.stock_qty) <= 0,
      }))
      .sort((a, b) => (a.out === b.out ? a.qty - b.qty : a.out ? -1 : 1))
  }, [products])

  const stockLoading = productState.isLoading && products.length === 0

  /* ── Orders by status ── */
  const orderBreakdown = useMemo(() => {
    const rows = ORDER_STATUSES.map((s) => ({
      ...s,
      count: orders.filter((o) => o.status === s.key).length,
    }))
    const max = Math.max(1, ...rows.map((r) => r.count))
    return { rows, max }
  }, [orders])

  const ordersLoading = orderState.isLoading && orders.length === 0

  /* ── Category tree: main -> sub-categories ── */
  const tree = useMemo(() => {
    const mains = categories.filter((c) => !c.parent_id)
    return mains
      .map((m, i) => {
        const subs = categories.filter((c) => c.parent_id === m.id)
        const ownCount = Number(m.products_count || 0)
        const subCount = subs.reduce((sum, c) => sum + Number(c.products_count || 0), 0)
        return {
          id: m.id,
          name: m.name,
          status: m.status,
          subs,
          total: ownCount + subCount,
          color: CAT_COLORS[i % CAT_COLORS.length],
        }
      })
      .sort((a, b) => b.total - a.total)
  }, [categories])

  // colors re-assign after sort so donut + bars match
  const treeColored = useMemo(
    () => tree.map((t, i) => ({ ...t, color: CAT_COLORS[i % CAT_COLORS.length] })),
    [tree]
  )

  const totalProducts = treeColored.reduce((s, t) => s + t.total, 0)
  const maxTotal = Math.max(1, ...treeColored.map((t) => t.total))
  const subTotal = categories.filter((c) => c.parent_id).length
  const activeOpen = openCat ?? treeColored[0]?.id

  const getFormattedDate = () =>
    new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date())

  return (
    <div className="dash-page page-in">

      {/* Header */}
      <div className="dash-hero">
        <div className="dash-hero-glow" />
        <div className="dash-ph">
          <div>
            <p className="dash-overline">Dashboard</p>
            <h1 className="page-title" style={{ marginBottom: 4 }}>Welcome back, Admin</h1>
            <p style={{ color: 'var(--text3)', fontSize: 'var(--fs-sm)' }}>
              {getFormattedDate()} · Here is the live performance snapshot.
            </p>
          </div>

          {/* Session Timer Block */}
          <div className={`session-timer-block${isWarn ? ' session-warn' : ''}`}>
            <div className="session-timer-top">
              <span className="session-icon">
                {isWarn ? <AlertTriangle size={14} /> : <Timer size={14} />}
              </span>
              <span className="session-label">{isWarn ? 'Expires soon' : 'Session active'}</span>
              <div className="session-time"> :- {timeStr}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Expires soon banner */}
      {isWarn && (
        <div className="warn-banner">
          <AlertTriangle size={15} />
          <span>Your session expires in <strong>{timeStr}</strong>. Click refresh on the timer to extend.</span>
        </div>
      )}

      {/* To do list */}
      <div className="card todo-section">
        <div className="card-header">
          <div className="todo-title-row">
            <span className="todo-title-icon"><ListChecks size={17} /></span>
            <div className="card-title">To do list</div>
          </div>
        </div>

        <div className="kpi-grid todo-grid">
          {todoStats.map(({ id, label, color, colorGlow, variant }) => (
            <div
              key={id}
              className={`stat-card todo-card stat-card--${variant}`}
            >
              <span style={{ position: 'absolute', top: 0, right: 0, width: 110, height: 110, borderRadius: '50%', background: colorGlow, filter: 'blur(30px)', transform: 'translate(30%,-30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)', position: 'relative' }}>
                <div className="stat-card-icon" style={{ background: colorGlow, color }}>{TODO_ICONS[id]}</div>
              </div>
              <div className="stat-card-value" style={{ position: 'relative', color }}>{todoValue(id)}</div>
              <div className="stat-card-label">{label}</div>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', background: `linear-gradient(90deg,${color}88,transparent)`, borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }} />
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="charts-row">
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">Revenue Overview</div><div className="card-subtitle">Monthly vs target</div></div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span className="leg"><span style={{ background: 'var(--accent)' }} />Revenue</span>
              <span className="leg"><span style={{ background: 'rgba(99,102,241,.35)' }} />Target</span>
              <span className="badge badge-gold">{year}</span>
            </div>
          </div>
          <div style={{ position: 'relative', height: 210 }}>
            {loading ? <div className="skeleton" style={{ height: '100%', borderRadius: 'var(--radius-md)' }} /> : <canvas ref={revenueRef} />}
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">Customer Acquisition</div><div className="card-subtitle">New vs verified this week</div></div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span className="leg"><span style={{ background: 'rgba(79,70,229,.8)' }} />New</span>
              <span className="leg"><span style={{ background: 'rgba(6,182,212,.5)' }} />Verified</span>
              <span className="badge badge-cyan">7 Days</span>
            </div>
          </div>
          <div style={{ position: 'relative', height: 210 }}>
            {custLoading ? <div className="skeleton" style={{ height: '100%', borderRadius: 'var(--radius-md)' }} /> : <canvas ref={userRef} />}
          </div>
        </div>
      </div>

      {/* Sales snapshot */}
      <div className="card ss-section">
        <div className="card-header">
          <div>
            <div className="card-title">Sales Snapshot</div>
            <div className="card-subtitle">Overall store performance &amp; latest orders</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/order')}>
            All orders <ArrowUpRight size={13} />
          </button>
        </div>

        <div className="ss-kpis">
          {[
            { l: 'Total Revenue', v: formatMoney(salesSummary.revenue), ic: <IndianRupee size={16} />, c: '#10B981', g: 'rgba(16,185,129,.15)' },
            { l: 'Total Orders', v: salesSummary.orders, ic: <ShoppingBag size={16} />, c: '#4F46E5', g: 'rgba(79,70,229,.15)' },
            { l: 'Avg. Order Value', v: formatMoney(salesSummary.avg), ic: <TrendingUp size={16} />, c: '#F59E0B', g: 'rgba(245,158,11,.15)' },
            { l: 'Customers', v: custLoading ? '…' : salesSummary.customers, ic: <Users size={16} />, c: '#06B6D4', g: 'rgba(6,182,212,.15)' },
          ].map((k) => (
            <div key={k.l} className="ss-kpi">
              <span className="ss-kpi-ic" style={{ background: k.g, color: k.c }}>{k.ic}</span>
              <div>
                <div className="ss-kpi-v">{loading ? '…' : k.v}</div>
                <div className="ss-kpi-l">{k.l}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="ss-fulfil">
          <div className="ss-fulfil-top">
            <span>Delivery completion</span>
            <b>{loading ? '…' : `${salesSummary.deliveredPct}%`}</b>
          </div>
          <div className="prog-track"><div className="prog-fill" style={{ width: `${salesSummary.deliveredPct}%`, background: '#10B981' }} /></div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead><tr><th>Order</th><th>Customer</th><th>Total</th><th>Status</th><th>Placed</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}>Loading…</td></tr>}
              {!loading && recentOrders.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text3)' }}>No orders yet</td></tr>
              )}
              {recentOrders.map((o) => {
                const st = ORDER_STATUSES.find((x) => x.key === o.status)
                return (
                  <tr key={o.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{o.order_number || o.order_ref || `#${o.id}`}</td>
                    <td style={{ fontWeight: 500, color: 'var(--text1)' }}>{o.customer_name || o.user_name || o.name || '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{formatMoney(o.total ?? o.total_amount ?? o.amount, o.currency)}</td>
                    <td>
                      <span className="ss-pill" style={{ background: `${st?.color || '#64748B'}22`, color: st?.color || '#64748B' }}>
                        <i style={{ background: st?.color || '#64748B' }} />{st?.label || o.status || '—'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>{timeAgo(o.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category overview */}
      <div className="card cat-ov">
        <div className="card-header">
          <div>
            <div className="card-title">Category Overview</div>
            <div className="card-subtitle">Products by main category &amp; sub-categories</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/category')}>
            Manage <ArrowUpRight size={13} />
          </button>
        </div>

        <div className="cat-mini-stats">
          <div className="cat-mini">
            <span className="cat-mini-ic" style={{ background: 'rgba(79,70,229,.15)', color: '#4F46E5' }}><Layers size={15} /></span>
            <div><div className="cat-mini-v">{treeColored.length}</div><div className="cat-mini-l">Main categories</div></div>
          </div>
          <div className="cat-mini">
            <span className="cat-mini-ic" style={{ background: 'rgba(6,182,212,.15)', color: '#06B6D4' }}><FolderTree size={15} /></span>
            <div><div className="cat-mini-v">{subTotal}</div><div className="cat-mini-l">Sub-categories</div></div>
          </div>
          <div className="cat-mini">
            <span className="cat-mini-ic" style={{ background: 'rgba(16,185,129,.15)', color: '#10B981' }}><Boxes size={15} /></span>
            <div><div className="cat-mini-v">{totalProducts}</div><div className="cat-mini-l">Products listed</div></div>
          </div>
        </div>

        {treeColored.length === 0 ? (
          <div className="cat-empty">No categories yet. Add your first category to see the breakdown here.</div>
        ) : (
          <div className="cat-body">
            <div className="cat-donut-col">
              <Donut segments={treeColored.map((t) => ({ id: t.id, value: t.total, color: t.color }))} total={totalProducts} />
            </div>

            <div className="cat-list">
              {treeColored.map((t) => {
                const open = activeOpen === t.id
                return (
                  <div key={t.id} className={`cat-item${open ? ' open' : ''}`}>
                    <button type="button" className="cat-row" onClick={() => setOpenCat(open ? 0 : t.id)}>
                      <span className="cat-dot" style={{ background: t.color }} />
                      <span className="cat-name">
                        {t.name}
                        {t.status === 0 && <span className="cat-off">inactive</span>}
                      </span>
                      <span className="cat-sub-count">{t.subs.length} sub</span>
                      <span className="cat-count">{t.total}</span>
                      <ChevronDown size={15} className="cat-chev" />
                    </button>
                    <div className="cat-bar">
                      <span style={{ width: `${(t.total / maxTotal) * 100}%`, background: t.color }} />
                    </div>

                    {open && (
                      <div className="cat-subs">
                        {t.subs.length === 0 && <span className="cat-nosub">No sub-categories</span>}
                        {t.subs.map((s) => (
                          <span key={s.id} className={`cat-chip${s.status === 0 ? ' off' : ''}`} style={{ '--c': t.color }}>
                            <i />
                            {s.name}
                            <b>{Number(s.products_count || 0)}</b>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Widgets */}
      <div className="widgets-row">
        <div className="card" style={{ padding: 0 }}>
          <div className="wdg-hdr">
            <span className="wdg-title">Recent Transactions</span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/transaction')}>View all</button>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr><th>ID</th><th>User</th><th>Order</th><th>Amount</th><th>Status</th><th>Time</th></tr></thead>
              <tbody>
                {txLoading && recentTx.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem' }}>Loading…</td></tr>
                )}
                {!txLoading && recentTx.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text3)' }}>No transactions yet</td></tr>
                )}
                {recentTx.map((tx) => {
                  const st = TX[tx.status] || TX.pending
                  return (
                    <tr key={tx.id}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{tx.txn_id}</td>
                      <td style={{ fontWeight: 500, color: 'var(--text1)' }}>{tx.user_name}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>{tx.order_ref || '—'}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>{formatMoney(tx.amount, tx.currency)}</td>
                      <td><span className={`badge ${st.cls}`}>{st.label}</span></td>
                      <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>{timeAgo(tx.paid_at || tx.created_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>

          {/* Stock Alerts (dynamic: products) */}
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Stock Alerts</div>
                <div className="card-subtitle">Out of stock &amp; running low</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/product')}>
                Manage <ArrowUpRight size={13} />
              </button>
            </div>

            {stockLoading && <div className="sa-empty">Loading…</div>}

            {!stockLoading && stockAlerts.length === 0 && (
              <div className="sa-empty sa-ok">
                <CheckCircle2 size={16} /> All products are well stocked
              </div>
            )}

            {stockAlerts.slice(0, 6).map((p, i, arr) => (
              <div key={p.id} className="sa-row" style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                <span
                  className="sa-ic"
                  style={{
                    background: p.out ? 'rgba(239,68,68,.15)' : 'rgba(245,158,11,.15)',
                    color: p.out ? '#EF4444' : '#F59E0B',
                  }}
                >
                  {p.out ? <PackageX size={13} /> : <PackageMinus size={13} />}
                </span>
                <span className="sa-name" title={p.name}>{p.name}</span>
                <span className={`sa-badge ${p.out ? 'out' : 'low'}`}>
                  {p.out ? 'Out of stock' : `${p.qty} left`}
                </span>
              </div>
            ))}

            {stockAlerts.length > 6 && (
              <div className="sa-more">+{stockAlerts.length - 6} more products need attention</div>
            )}
          </div>

          {/* Orders by status (dynamic: orders) */}
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Orders by Status</div>
                <div className="card-subtitle">{ordersLoading ? 'Loading…' : `${orders.length} orders in total`}</div>
              </div>
              <span className="todo-title-icon"><ClipboardList size={15} /></span>
            </div>

            {orderBreakdown.rows.map((r) => (
              <div key={r.key} className="os-row">
                <div className="os-top">
                  <span className="os-dot" style={{ background: r.color }} />
                  <span className="os-label">{r.label}</span>
                  <span className="os-count">{ordersLoading ? '…' : r.count}</span>
                </div>
                <div className="prog-track">
                  <div className="prog-fill" style={{ width: `${(r.count / orderBreakdown.max) * 100}%`, background: r.color }} />
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }
        .dash-hero {
          position: relative;
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-active);
          background: linear-gradient(135deg, rgba(79, 70, 229, .2), rgba(6, 182, 212, .12));
          overflow: hidden;
          padding: clamp(1rem, 2vw, 1.5rem);
        }
        .dash-overline { font-size:var(--fs-xs); text-transform:uppercase; letter-spacing:.08em; color:var(--accent-hover); font-weight:700; margin-bottom:4px; }
        .dash-hero-glow { position:absolute; width:280px; height:280px; right:-70px; top:-90px; border-radius:50%; background:rgba(79,70,229,.22); filter:blur(45px); pointer-events:none; }
        .dash-ph { display:flex; justify-content:space-between; align-items:flex-end; flex-wrap:wrap; gap:var(--space-md); }
        .kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:var(--space-md); }
        .widgets-row { display:grid; grid-template-columns:1.5fr 1fr; gap:var(--space-md); align-items:start; }
        .wdg-hdr { display:flex; justify-content:space-between; align-items:center; padding:var(--space-md) var(--space-lg); border-bottom:1px solid var(--border); }
        .wdg-title { font-size:var(--fs-md); font-weight:600; color:var(--text1); }
        .leg { display:flex; align-items:center; gap:5px; font-size:var(--fs-xs); color:var(--text3); }
        .leg span { width:8px; height:8px; border-radius:2px; flex-shrink:0; display:inline-block; }
        .prog-track { height:5px; background:var(--bg-raised); border-radius:var(--radius-full); overflow:hidden; }
        .prog-fill { height:100%; border-radius:var(--radius-full); transition:width .8s cubic-bezier(.4,0,.2,1); }

        /* To do list */
        .todo-section { display: flex; flex-direction: column; }
        .todo-section .card-header { margin-bottom: var(--space-lg); }
        .todo-grid { margin: 0; }
        .todo-title-row { display:flex; align-items:center; gap:8px; }
        .todo-title-icon {
          width: 30px; height: 30px; border-radius: var(--radius-sm);
          display: flex; align-items: center; justify-content: center;
          background: var(--bg-raised); border: 1px solid var(--border);
          color: var(--text2); flex-shrink: 0;
        }
        .todo-card { text-align: left; font-family: inherit; width: 100%; cursor: default; }

        /* ── Stock Alerts ── */
        .sa-row { display:flex; align-items:center; gap:10px; padding:9px 0; }
        .sa-ic { width:26px; height:26px; border-radius:var(--radius-sm); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .sa-name { flex:1; min-width:0; font-size:var(--fs-sm); font-weight:500; color:var(--text1); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .sa-badge { font-size:11px; font-weight:700; padding:2px 9px; border-radius:99px; white-space:nowrap; flex-shrink:0; }
        .sa-badge.out { background:rgba(239,68,68,.15); color:#EF4444; }
        .sa-badge.low { background:rgba(245,158,11,.15); color:#F59E0B; }
        .sa-empty { padding:var(--space-md) 0; text-align:center; color:var(--text3); font-size:var(--fs-sm); }
        .sa-ok { display:flex; align-items:center; justify-content:center; gap:8px; color:#10B981; }
        .sa-more { margin-top:8px; padding-top:10px; border-top:1px solid var(--border-subtle); font-size:var(--fs-xs); color:var(--text3); text-align:center; }

        /* ── Orders by status ── */
        .os-row { margin-bottom:14px; }
        .os-row:last-child { margin-bottom:0; }
        .os-top { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
        .os-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .os-label { flex:1; font-size:var(--fs-sm); color:var(--text2); }
        .os-count { font-size:var(--fs-sm); font-weight:700; color:var(--text1); }

        /* ── Sales snapshot ── */
        .ss-kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:var(--space-sm); margin-bottom:var(--space-md); }
        .ss-kpi { display:flex; align-items:center; gap:12px; padding:12px 14px; border:1px solid var(--border); border-radius:var(--radius-md); background:var(--bg-raised); min-width:0; }
        .ss-kpi-ic { width:36px; height:36px; border-radius:var(--radius-sm); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .ss-kpi-v { font-size:var(--fs-lg, 18px); font-weight:700; color:var(--text1); line-height:1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .ss-kpi-l { font-size:var(--fs-xs); color:var(--text3); margin-top:2px; }
        .ss-fulfil { margin-bottom:var(--space-lg); }
        .ss-fulfil-top { display:flex; justify-content:space-between; font-size:var(--fs-sm); color:var(--text2); margin-bottom:6px; }
        .ss-fulfil-top b { color:var(--text1); }
        .ss-pill { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:700; padding:3px 10px; border-radius:99px; }
        .ss-pill i { width:6px; height:6px; border-radius:50%; display:inline-block; }

        /* ── Category overview ── */
        .cat-mini-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:var(--space-sm); margin-bottom:var(--space-lg); }
        .cat-mini {
          display:flex; align-items:center; gap:10px;
          padding:10px 12px; border:1px solid var(--border);
          border-radius:var(--radius-md); background:var(--bg-raised);
        }
        .cat-mini-ic { width:32px; height:32px; border-radius:var(--radius-sm); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .cat-mini-v { font-size:var(--fs-lg, 18px); font-weight:700; color:var(--text1); line-height:1.1; }
        .cat-mini-l { font-size:var(--fs-xs); color:var(--text3); }

        .cat-body { display:grid; grid-template-columns:200px 1fr; gap:var(--space-lg); align-items:center; }
        .cat-donut-col { display:flex; justify-content:center; }
        .donut-wrap { position:relative; width:170px; height:170px; }
        .donut-svg { width:100%; height:100%; }
        .donut-center { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
        .donut-num { font-size:26px; font-weight:800; color:var(--text1); line-height:1; }
        .donut-lbl { font-size:var(--fs-xs); color:var(--text3); margin-top:4px; text-transform:uppercase; letter-spacing:.06em; }

        .cat-list { display:flex; flex-direction:column; gap:6px; min-width:0; }
        .cat-item { padding:8px 10px; border:1px solid transparent; border-radius:var(--radius-md); transition:background .2s, border-color .2s; }
        .cat-item:hover, .cat-item.open { background:var(--bg-raised); border-color:var(--border); }
        .cat-row {
          width:100%; display:flex; align-items:center; gap:10px;
          background:transparent; border:0; cursor:pointer; padding:0;
          color:var(--text1); font-family:inherit; text-align:left;
        }
        .cat-dot { width:10px; height:10px; border-radius:3px; flex-shrink:0; }
        .cat-name { flex:1; min-width:0; font-weight:600; font-size:var(--fs-sm); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .cat-off { margin-left:8px; font-size:10px; font-weight:600; color:var(--text3); border:1px solid var(--border); border-radius:10px; padding:1px 6px; }
        .cat-sub-count { font-size:var(--fs-xs); color:var(--text3); }
        .cat-count { font-weight:700; font-size:var(--fs-sm); min-width:28px; text-align:right; }
        .cat-chev { color:var(--text3); transition:transform .2s; }
        .cat-item.open .cat-chev { transform:rotate(180deg); }
        .cat-bar { height:5px; background:var(--bg-hover, rgba(128,128,128,.15)); border-radius:99px; overflow:hidden; margin-top:8px; }
        .cat-bar span { display:block; height:100%; border-radius:99px; transition:width .8s cubic-bezier(.4,0,.2,1); }
        .cat-subs { display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
        .cat-nosub { font-size:var(--fs-xs); color:var(--text3); }
        .cat-chip {
          display:inline-flex; align-items:center; gap:6px;
          font-size:var(--fs-xs); color:var(--text2);
          padding:4px 6px 4px 8px; border-radius:99px;
          border:1px solid var(--border); background:var(--bg-card, transparent);
        }
        .cat-chip i { width:6px; height:6px; border-radius:50%; background:var(--c); }
        .cat-chip b { background:var(--c); color:#fff; font-size:10px; min-width:18px; text-align:center; padding:1px 6px; border-radius:99px; }
        .cat-chip.off { opacity:.55; }
        .cat-empty { padding:var(--space-lg); text-align:center; color:var(--text3); font-size:var(--fs-sm); }

        /* Session Timer */
        .session-timer-block {
          display: flex; justify-content: center; align-items: center; align-self: center;
          gap: 5px; border: 1px solid var(--border); border-radius: var(--radius-md);
          padding: 10px 14px; min-width: 170px;
          transition: border-color .2s, background .2s; backdrop-filter: blur(10px);
        }
        .session-timer-block.session-warn { border-color: rgba(245,158,11,.45); background: rgba(245,158,11,.07); }
        .session-timer-top { display: flex; align-items: center; gap: 6px; width: 100%; }
        .session-icon { display: flex; align-items: center; color: #6D7AA5; flex-shrink: 0; }
        .session-warn .session-icon { color: var(--color-amber); }
        .session-label { font-size: var(--fs-xs); font-weight: 600; color: #6D7AA5; text-transform: uppercase; letter-spacing: .07em; flex: 1; }
        .session-warn .session-label { color: var(--color-amber); }
        .session-time { font-family: var(--font-mono); font-size: var(--fs-xl); font-weight: 800; color: var(--text1); letter-spacing: -0.02em; line-height: 1; align-self: flex-start; }
        .session-warn .session-time { color: var(--color-amber); }

        /* Warn Banner */
        .warn-banner {
          display: flex; align-items: center; gap: 10px;
          background: rgba(245,158,11,.1); border: 1px solid rgba(245,158,11,.3);
          border-radius: var(--radius-md); padding: 10px 16px;
          font-size: var(--fs-sm); color: var(--color-amber); animation: soft-rise .3s ease;
        }
        .warn-banner strong { font-weight: 700; }

        @media(max-width:1100px){ .kpi-grid{grid-template-columns:repeat(2,1fr);} .widgets-row{grid-template-columns:1fr;} }
        @media(max-width:768px){ .ss-kpis{grid-template-columns:repeat(2,1fr);} .cat-body{grid-template-columns:1fr;} .cat-mini-stats{grid-template-columns:1fr;} }
        @media(max-width:600px){ .session-timer-block{min-width:140px;} .session-time{font-size:var(--fs-xl);} }
      `}</style>
    </div>
  )
}